import { useOutletContext } from "react-router-dom";
import type { StaffCapability } from "@fd/contracts";

/** Mirrors the API's StaffRole (apps/api/src/middleware/entra-auth.ts). */
export type StaffRole = "sales" | "admin" | "csm";

/** Mirrors the API's Capability catalog (apps/api/src/services/access.ts). */
export type Capability = StaffCapability;

export interface Staff {
  email: string;
  display_name: string;
  role: StaffRole;
  roles: string[];
  /**
   * Effective access levels resolved server-side (role default + per-person
   * override; admins hold all). Used to show/hide capability-gated surfaces.
   */
  capabilities: Capability[];
}

/** Provided by AdminShell via `<Outlet context>` to every admin route. */
export interface AdminOutletContext {
  staff: Staff;
  registerNavigationGuard?: (guard: (() => boolean) | null) => void;
}

/** The signed-in staff user. Only valid inside the AdminShell outlet. */
export function useStaff(): Staff {
  return useOutletContext<AdminOutletContext>().staff;
}

/** Offer actions follow the effective permission returned by the API. */
export function canWriteOffers(staff: Staff): boolean {
  return can(staff, "offers.manage");
}

/** Manage customer users; destructive account actions still require Admin. */
export function canManageUsers(staff: Staff): boolean {
  return can(staff, "users.manage");
}

/** Read visibility is unchanged; only the churn-risk action needs this grant. */
export function canFlagChurnRisk(staff: Staff): boolean {
  return can(staff, "clients.churn_risk");
}

/** Schedule non-renewal independently of the broader contract-change permission. */
export function canTerminateContract(staff: Staff): boolean {
  return can(staff, "contracts.terminate");
}

/** Corrected invoice reissues require the effective invoice-amendment permission. */
export function canAmendInvoice(staff: Staff): boolean {
  return can(staff, "invoices.amend");
}

/**
 * Whether the staff member holds a capability (access level). Mirrors the API's
 * resolution: admins always pass; everyone else is checked against the resolved set
 * the server returned on /me. Keeps the UI from rendering surfaces that would 403.
 */
export function can(staff: Staff, cap: Capability): boolean {
  return staff.role === "admin" || (staff.capabilities?.includes(cap) ?? false);
}
